/// <amd-module name="MarkdownEditor/Components/MarkdownToolbar" />
import { IMarkdownToolbarProps } from "MarkdownEditor/Components/MarkdownToolbar.Props";
import * as Resources from "MarkdownEditor/Resources";
import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { MenuButton } from "VSSUI/Menu";
import * as React from "react";
import { MarkdownSyntax, MarkdownToolbarHelper } from "Utilities/MarkdownToolbarHelper";
import { KeyCode } from "VSSUI/Util";
import { format } from "VSS/Core/Util/String";
import { Location } from "VSSUI/Utilities/Position";

export class MarkdownToolbar extends React.PureComponent<IMarkdownToolbarProps> {
    private _hiddenUploadInput: HTMLInputElement;

    public render(): JSX.Element {
        const buttonClass = "markdown-toolbar-button";
        const disabled = this.props.disabled;
        const menuItemClass = disabled ? "markdown-toolbar-menuitem-disabled" : "";

        return (
            <>
                <ButtonGroup className="flex-wrap markdown-editor-toolbar">
                    <MenuButton
                        id="header-menu-item"
                        className={menuItemClass}
                        disabled={disabled}
                        ariaLabel={Resources.MarkdownHeader}
                        subtle={true}
                        iconProps={{ iconName: "FontSize", className: buttonClass }}
                        autoFocus={true}
                        tooltipProps={{
                            addAriaDescribedBy: true,
                            text: Resources.MarkdownHeader
                        }}
                        contextualMenuProps={{
                            anchorOrigin: { horizontal: Location.start, vertical: Location.end },
                            menuOrigin: { horizontal: Location.start, vertical: Location.start },
                            menuProps: {
                                id: "header-menu-items",
                                items: [
                                            {
                                                id: "header1-menu-item",
                                                text: format(Resources.MarkdownHeaderN, 1),
                                                ariaLabel: format(Resources.MarkdownHeaderN, 1),
                                                className: "markdowntoolbar-header1",
                                                onActivate: this._insertHeader1MarkdownSyntax
                                            },
                                            {
                                                id: "header2-menu-item",
                                                text: format(Resources.MarkdownHeaderN, 2),
                                                ariaLabel: format(Resources.MarkdownHeaderN, 2),
                                                className: "markdowntoolbar-header2",
                                                onActivate: this._insertHeader2MarkdownSyntax
                                            },
                                            {
                                                id: "header3-menu-item",
                                                text: format(Resources.MarkdownHeaderN, 3),
                                                ariaLabel: format(Resources.MarkdownHeaderN, 3),
                                                className: "markdowntoolbar-header3",
                                                onActivate: this._insertHeader3MarkdownSyntax
                                            }
                                        ]
                            }
                        }}
                    />
                    <Button
                        id="bold-menu-item"
                        className={menuItemClass}
                        disabled={disabled}
                        ariaLabel={Resources.MarkdownBold}
                        subtle={true}
                        onClick={this._onBold}
                        iconProps={{ iconName: "Bold", className: buttonClass }}
                    />
                    <Button
                        id="italic-menu-item"
                        className={menuItemClass}
                        disabled={disabled}
                        ariaLabel={Resources.MarkdownItalic}
                        subtle={true}
                        onClick={this._onItalic}
                        iconProps={{ iconName: "Italic", className: buttonClass }}
                    />
                    <Button
                        id="link-menu-item"
                        className={menuItemClass}
                        disabled={disabled}
                        ariaLabel={Resources.MarkdownLink}
                        subtle={true}
                        onClick={this._onLink}
                        iconProps={{ iconName: "Link", className: buttonClass }}
                    />
                    <Button
                        id="code-menu-item"
                        className={menuItemClass}
                        disabled={disabled}
                        ariaLabel={Resources.MarkdownCode}
                        subtle={true}
                        onClick={this._onCode}
                        iconProps={{ iconName: "Code", className: buttonClass }}
                    />
                    <Button
                        id="bullet-menu-item"
                        className={menuItemClass}
                        disabled={disabled}
                        ariaLabel={Resources.MarkdownBulletedList}
                        subtle={true}
                        onClick={this._onBulletedList}
                        iconProps={{ iconName: "BulletedList", className: buttonClass }}
                    />
                    <Button
                        id="numbered-menu-item"
                        className={menuItemClass}
                        disabled={disabled}
                        ariaLabel={Resources.MarkdownNumberedList}
                        subtle={true}
                        onClick={this._onNumberedList}
                        iconProps={{ iconName: "NumberedList", className: buttonClass }}
                    />
                    <Button
                        id="tasklist-menu-item"
                        className={menuItemClass}
                        disabled={disabled}
                        ariaLabel={Resources.MarkdownTaskList}
                        subtle={true}
                        onClick={this._onTaskList}
                        iconProps={{ iconName: "CheckList", className: buttonClass }}
                    />
                    {this.props.enableAttachments ?
                        <Button
                            id="attach-menu-item"
                            className={menuItemClass}
                            disabled={disabled}
                            ariaLabel={Resources.AttachmentHelpText}
                            subtle={true}
                            onClick={this._onUploadClick}
                            iconProps={{ iconName: "Attach", className: buttonClass }}
                        />
                        : null
                    }
                </ButtonGroup>
                {this._drawAttachmentsInput()}
            </>
        );
    }

    public handleKeyPress(event: React.KeyboardEvent<HTMLElement>): boolean {
        if (event.ctrlKey && event.keyCode === KeyCode.b) {
            this._onBold();
            event.preventDefault();
            event.stopPropagation();
            return true;
        } else if (event.ctrlKey && event.keyCode === KeyCode.i) {
            this._onItalic();
            event.preventDefault();
            event.stopPropagation();
            return true;
        } else if (event.ctrlKey && event.keyCode === KeyCode.k) {
            this._onLink();
            event.preventDefault();
            event.stopPropagation();
            return true;
        } else if (this.props.onKeyDown) {
            this.props.onKeyDown(event);
        }

        return false;
    }

    public insertText(insertedString: string, postString: string): void {
        const textArea = this.props.getTextArea();
        if (!textArea) {
            return;
        }

        const currentText = textArea.value;
        const selectionStart = textArea.selectionStart;
        const selectionEnd = textArea.selectionEnd;
        let newText = insertedString;

        textArea.focus();
        if (MarkdownToolbarHelper.browserSupportsInsertText()) {
            // If the browser supports it, insertText is a better way to programatically change the text which supports undo/redo
            // This will also trigger any events on the parent control as if the user typed in this text
            document.execCommand("insertText", false, newText + postString);
            if (postString) {
                let cursorPosition = textArea.selectionStart;
                cursorPosition -= postString.length;
                textArea.selectionStart = textArea.selectionEnd = cursorPosition;
            }
            this.props.textChanged(null, textArea.selectionStart, textArea.selectionEnd);
        } else {
            const firstPart = currentText.substring(0, selectionStart);
            const endPart = currentText.substring(selectionEnd);
            const newCursorPosition = firstPart.length + newText.length;

            // otherwise, the parent control needs to deal with the new text
            newText += postString;
            this.props.textChanged(firstPart + newText + endPart, newCursorPosition, newCursorPosition);
        }
    }

    private _insertHeader1MarkdownSyntax = () => {
        MarkdownToolbarHelper.insertTextAtStartOfEachLine(this.props.getTextArea(), MarkdownSyntax.Header1, this.props.textChanged, true, true);
    };

    private _insertHeader2MarkdownSyntax = () => {
        MarkdownToolbarHelper.insertTextAtStartOfEachLine(this.props.getTextArea(), MarkdownSyntax.Header2, this.props.textChanged, true, true);
    };

    private _insertHeader3MarkdownSyntax = () => {
        MarkdownToolbarHelper.insertTextAtStartOfEachLine(this.props.getTextArea(), MarkdownSyntax.Header3, this.props.textChanged, true, true);
    };

    private _onBold = () => {
        MarkdownToolbarHelper.wrapText(this.props.getTextArea(), MarkdownSyntax.Bold, this.props.textChanged);
    };

    private _onItalic = () => {
        MarkdownToolbarHelper.wrapText(this.props.getTextArea(), MarkdownSyntax.Italic, this.props.textChanged);
    };

    // This whole roundabout nonsense is to work around an edge bug that makes the file browse dialog pop up infinity times
    // https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/8282613/
    // The better approach is to do away with hidden upload input and make the encompassing div a label
    private _onUploadClick = () => {
        if (this._hiddenUploadInput) {
            this._hiddenUploadInput.click();
        }
    };

    private _onFileBrowse = (event: any) => {
        if (!this.props.addAttachments) {
            return;
        }

        const textArea = this.props.getTextArea();
        if (!textArea) {
            return;
        }

        const fileList: File[] = [];
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < event.target.files.length; i += 1) {
            fileList.push(event.target.files[i]);
        }

        this.props.addAttachments(fileList, textArea.selectionStart || 0, textArea.selectionEnd || 0);

        // Triggers extra event in ie 11 with value empty file list.
        event.target.value = null;

        event.stopPropagation();
        event.preventDefault();

        textArea && textArea.focus();
    };

    private _onAttachmentsInputChange = (e: any) => {
        e && e.target && e.target.value && e.target.value.length > 0 && this._onFileBrowse(e);
    };

    private _drawAttachmentsInput(): JSX.Element {
        return (
            <input
                style={{ display: "none" }}
                type="file"
                accept={this.props.validAttachmentTypes ? this.props.validAttachmentTypes.toString() : ""}
                ref={hiddenUploadInput => {
                    if (hiddenUploadInput) {
                        this._hiddenUploadInput = hiddenUploadInput;
                    }
                }}
                onChange={this._onAttachmentsInputChange}
            />
        );
    }

    private _onLink = () => {
        const textArea = this.props.getTextArea();
        if (!textArea) {
            return;
        }

        const currentText = textArea.value;
        let selectionStart = textArea.selectionStart || 0;
        let selectionEnd = textArea.selectionEnd || 0;
        let haveSelection = selectionStart !== selectionEnd;

        if (!haveSelection) {
            const expandResults = MarkdownToolbarHelper.expandCursorToWord(currentText, selectionStart);
            selectionStart = expandResults.start;
            selectionEnd = expandResults.end;

            haveSelection = selectionStart !== selectionEnd;
        } else {
            // If we do have a selection, trim beginning/trailing whitespace
            while (selectionStart < selectionEnd && /\s/.test(currentText[selectionStart])) {
                selectionStart += 1;
            }

            while (selectionEnd > selectionStart && /\s/.test(currentText[selectionEnd - 1])) {
                selectionEnd -= 1;
            }
        }

        let insertedString: string;
        let postString: string;

        // If we have a selection, wrap it inside [] and move the cursor to be inside ()
        // If we don't have a selection, put the cursor inside []
        if (haveSelection) {
            insertedString = `[${currentText.substring(selectionStart, selectionEnd)}](`;
            postString = ")";
        } else {
            insertedString = "[";
            postString = "]()";
        }

        textArea.selectionStart = selectionStart;
        textArea.selectionEnd = selectionEnd;

        this.insertText(insertedString, postString);
    };

    private _onCode = () => {
        const textArea = this.props.getTextArea();
        if (!textArea) {
            return;
        }

        const selectionStart = textArea.selectionStart;
        const selectionEnd = textArea.selectionEnd;
        const currentText = textArea.value;
        const selectedText = currentText.substring(selectionStart, selectionEnd);
        const codeType = this._isMultiline(selectedText) ? MarkdownSyntax.CodeBlock : MarkdownSyntax.Code;
        MarkdownToolbarHelper.wrapText(textArea, codeType, this.props.textChanged);
    };

    private _onBulletedList = () => {
        MarkdownToolbarHelper.insertTextAtStartOfEachLine(
            this.props.getTextArea(),
            MarkdownSyntax.BulletedList,
            this.props.textChanged,
            true,
            false,
            true
        );
    };

    private _onNumberedList = () => {
        MarkdownToolbarHelper.insertNumberedList(this.props.getTextArea(), this.props.textChanged);
    };

    private _onTaskList = () => {
        MarkdownToolbarHelper.insertTextAtStartOfEachLine(
            this.props.getTextArea(),
            MarkdownSyntax.TaskList,
            this.props.textChanged,
            true,
            false,
            true
        );
    };

    private _isMultiline(str: string): boolean {
        const strAfterWhitespaceRemoval = str.trim();
        return strAfterWhitespaceRemoval.indexOf("\n") >= 0;
    }
}
